import { Badge } from "@/components/ui/badge";
import WaitlistForm from "./WaitlistForm";
import { useInView } from "@/hooks/useInView";
import { Bot, ArrowRight } from "lucide-react";

const Hero = () => {
  const { ref, isInView } = useInView({ threshold: 0.1 });

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col items-center justify-center pt-20 pb-16 bg-background overflow-hidden" aria-labelledby="hero-heading">
      {/* Animated dot pattern background */}
      <div
        className="absolute inset-0 opacity-[0.15]"
        style={{
          backgroundImage: "radial-gradient(circle, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* Floating gradient orbs */}
      <div
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full opacity-[0.04] blur-3xl pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(var(--foreground)), transparent 70%)",
          animation: "float 15s ease-in-out infinite",
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-[0.03] blur-3xl pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(var(--foreground)), transparent 70%)",
          animation: "float 20s ease-in-out infinite reverse",
        }}
      />

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className={`transition-all duration-700 ease-out ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-sm gap-2">
              <Bot className="w-3.5 h-3.5" />
              AI-Powered Agent
            </Badge>
          </div>

          {/* Headline */}
          <h1
            id="hero-heading"
            className={`text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-[1.1] mb-6 tracking-tight transition-all duration-700 ease-out delay-100 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          >
            Your AI agent that optimizes your website 24/7
          </h1>

          {/* Subtitle */}
          <p className={`text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 ease-out delay-200 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            Tell your agent what you want. It analyzes your site, personalizes content, runs A/B tests, and optimizes conversions — automatically.
          </p>

          {/* Waitlist Form */}
          <div className={`mb-16 transition-all duration-700 ease-out delay-300 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <WaitlistForm source="hero" variant="hero" />
            <p className="text-sm text-muted-foreground mt-4">
              Join the waitlist. Early access for the first 500.
            </p>
          </div>

          {/* Agent capabilities preview */}
          <div className={`transition-all duration-700 ease-out delay-[400ms] ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="max-w-2xl mx-auto bg-muted/50 rounded-2xl border border-border p-6 text-left">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-chart-2 animate-pulse" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Agent Activity — Live</span>
              </div>
              <div className="space-y-3">
                {[
                  { action: "Detected high bounce rate on mobile", result: "Created welcome banner → +18% engagement", time: "2m ago" },
                  { action: "Instagram visitors convert 3x more", result: "Built personalized landing for utm_source=instagram", time: "1h ago" },
                  { action: "Campaign 'Summer Sale' CTR dropped to 0.3%", result: "Generated 3 copy variants, running A/B test", time: "3h ago" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 group">
                    <ArrowRight className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0 group-hover:text-foreground transition-colors" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">{item.action}</p>
                      <p className="text-xs text-muted-foreground">{item.result}</p>
                    </div>
                    <span className="text-xs text-muted-foreground/60 flex-shrink-0">{item.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
